// src/summaries.js
// Lazy AI summary cache for activities (notes, calls, emails, meetings).
//
// Architecture:
//   - One blob key: `crm_activity_summaries` = { [activityId]: summary }
//   - In-memory `cache` holds the loaded map for fast sync reads
//   - First app boot loads the blob once into `cache`
//   - getSummary(id, content) returns cached value sync if present,
//     otherwise generates via Claude (async), stores, and notifies subscribers
//
// Important behaviors per user spec:
//   - 2 sentences max
//   - Skip summarization if content is too short / empty / future-dated
//   - On error -> "no summary available"
//   - Summaries are immutable once generated (no auto-refresh)

import { aiCall, serverSave, serverLoad } from "./helpers.js";

const STORAGE_KEY = "crm_activity_summaries";
const MIN_CONTENT_LENGTH = 60;  // skip summaries for very short notes (Q3)
const FALLBACK_TEXT = "(no summary available)";

// In-memory cache. {activityId: summary}
let cache = {};
let cacheLoaded = false;       // true once initial blob load resolves
let loadPromise = null;        // dedupe concurrent boot loads
let dirtyKeys = new Set();     // newly-generated summaries waiting to persist
let saveTimer = null;          // debounced save handle

// Active generation promises (dedupe simultaneous requests for same ID).
// Map<activityId, Promise<string>>
const inFlight = new Map();

// Subscribers for re-render notifications (used by React components via hook).
// Set<callback>
const subscribers = new Set();
function notify() { subscribers.forEach(fn => fn()); }
export function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

// Load cache from blob storage on first call. Subsequent calls return same
// promise (memoized).
export function loadCache() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const data = await serverLoad(STORAGE_KEY);
      if (data && typeof data === "object") cache = data;
    } catch (e) {
      console.warn("Activity summary cache load failed:", e.message);
    }
    cacheLoaded = true;
    notify();
  })();
  return loadPromise;
}

// Debounced save of dirty keys to blob storage. Batches writes that happen
// within 2 seconds together to avoid hammering Azure Blob during heavy
// summarization sessions.
function scheduleSave() {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    saveTimer = null;
    if (dirtyKeys.size === 0) return;
    // Take a snapshot, clear the dirty set, then persist the full cache.
    // If save fails we re-mark those keys dirty for retry.
    const snapshot = new Set(dirtyKeys);
    dirtyKeys.clear();
    try {
      await serverSave(STORAGE_KEY, cache);
    } catch (e) {
      console.warn("Summary cache save failed:", e.message);
      snapshot.forEach(k => dirtyKeys.add(k));
      // Retry in 30s
      setTimeout(scheduleSave, 30000);
    }
  }, 2000);
}

// Strip HTML tags and trim whitespace for AI input.
function cleanContent(s) {
  return String(s || "")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Decide whether a piece of content is even worth summarizing.
//   - empty / null  -> false
//   - shorter than MIN_CONTENT_LENGTH -> false (just show as-is)
//   - future-dated activity (Q6)      -> caller responsibility, not here
function shouldSummarize(content) {
  const clean = cleanContent(content);
  return clean.length >= MIN_CONTENT_LENGTH;
}

// Synchronous lookup. Returns:
//   - the cached summary string if present
//   - undefined if not yet generated (caller should kick off generation)
//   - null if the content was deemed too short / not summarizable
export function getCachedSummary(activityId, content) {
  if (!activityId) return null;
  if (!shouldSummarize(content)) return null;
  return cache[activityId];   // undefined if not cached yet
}

// Async generate-or-fetch. Returns the summary or FALLBACK_TEXT on failure.
// Deduplicates concurrent calls for the same activityId so we don't make
// 20 simultaneous API calls when a contact card opens.
export async function getSummary(activityId, content) {
  if (!activityId) return FALLBACK_TEXT;
  if (!shouldSummarize(content)) return null;

  // Ensure cache is loaded (first call to anything triggers boot load)
  if (!cacheLoaded) await loadCache();

  if (cache[activityId] !== undefined) return cache[activityId];

  // De-dupe in-flight for same ID
  if (inFlight.has(activityId)) return inFlight.get(activityId);

  const promise = (async () => {
    const clean = cleanContent(content);
    // Trim very long content to keep AI cost predictable. Claude can handle
    // a lot, but call/email logs can be huge -- 2000 chars is plenty for a
    // 2-sentence summary.
    const trimmed = clean.length > 2000 ? clean.slice(0, 2000) + "..." : clean;
    const prompt =
      "Summarize the following sales activity in exactly 2 short sentences. " +
      "Focus on what was discussed and any next steps. No preamble, no markdown, " +
      "no quotation marks. Just the 2 sentences.\n\n" +
      "Content:\n" + trimmed;
    try {
      const r = await aiCall(prompt);
      const text = r?.content?.find(b => b.type === "text")?.text || "";
      const summary = text.trim().replace(/^["']|["']$/g, "");
      if (!summary) {
        cache[activityId] = FALLBACK_TEXT;
      } else {
        cache[activityId] = summary;
      }
    } catch (e) {
      console.warn("AI summarize failed for", activityId, ":", e.message);
      cache[activityId] = FALLBACK_TEXT;
    }
    dirtyKeys.add(activityId);
    scheduleSave();
    inFlight.delete(activityId);
    notify();
    return cache[activityId];
  })();
  inFlight.set(activityId, promise);
  return promise;
}
